import { useEffect, useState } from "react";
import api from "@/lib/api";
import { JOURNAL } from "@/constants/testIds";
import PostTradeReviewModal from "@/components/PostTradeReviewModal";
import { CheckCircle2, AlertCircle } from "lucide-react";

export default function Journal() {
  const [items, setItems] = useState([]);
  const [reviewFor, setReviewFor] = useState(null);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    const { data } = await api.get("/journal");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const openDetail = async (jid) => {
    setSelected(jid);
    const { data } = await api.get(`/journal/${jid}`);
    setDetail(data);
  };

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid={JOURNAL.root}>
      <h1 className="text-xl font-medium tracking-tight">Trade Journal</h1>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-px bg-border/60">
        <section className="lg:col-span-7 bg-card">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-widest text-muted-foreground bg-background/20">
              <tr>
                <th className="text-left px-4 py-1.5 font-normal">Symbol</th>
                <th className="text-right px-4 py-1.5 font-normal">Entry</th>
                <th className="text-right px-4 py-1.5 font-normal">Exit</th>
                <th className="text-right px-4 py-1.5 font-normal">Realized</th>
                <th className="text-right px-4 py-1.5 font-normal">Review</th>
                <th className="text-right px-4 py-1.5 font-normal"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((j) => (
                <tr key={j.id} onClick={() => openDetail(j.id)}
                    className={`border-t border-border/40 cursor-pointer hover:bg-secondary/30 ${selected===j.id?"bg-secondary/40":""}`}
                    data-testid={JOURNAL.row(j.id)}>
                  <td className="px-4 py-2 mono">{j.symbol}</td>
                  <td className="px-4 py-2 mono text-right">${j.entry_price?.toFixed(2)}</td>
                  <td className="px-4 py-2 mono text-right">{j.exit_price ? `$${j.exit_price.toFixed(2)}` : "—"}</td>
                  <td className={`px-4 py-2 mono text-right ${(j.realized_pnl ?? 0) >= 0 ? "pnl-up" : "pnl-down"}`}>{j.realized_pnl !== null && j.realized_pnl !== undefined ? (j.realized_pnl >= 0 ? "+" : "") + "$" + j.realized_pnl?.toFixed(2) : "—"}</td>
                  <td className="px-4 py-2 text-right text-xs mono uppercase tracking-widest">
                    {j.exit_price ? (j.reviewed ? <span className="pnl-up inline-flex items-center gap-1"><CheckCircle2 size={12}/>Done</span> : <span className="text-signal-yellow inline-flex items-center gap-1"><AlertCircle size={12}/>Pending</span>) : <span className="text-muted-foreground">Open</span>}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {j.exit_price && !j.reviewed && <button className="btn-outline h-7 !px-2 text-[11px]" onClick={(e)=>{e.stopPropagation(); setReviewFor(j.id);}}>Review</button>}
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-xs text-muted-foreground">No trades journaled yet.</td></tr>}
            </tbody>
          </table>
        </section>

        <section className="lg:col-span-5 bg-card p-4">
          {!detail && <div className="text-xs text-muted-foreground">Select a trade to view full context.</div>}
          {detail && (
            <div className="space-y-4">
              <div>
                <div className="flex items-center gap-3">
                  <div className="mono text-lg">{detail.journal.symbol}</div>
                  <div className="text-[10px] mono uppercase text-muted-foreground tracking-widest">
                    ×{detail.journal.qty} · ENT ${detail.journal.entry_price?.toFixed(2)}
                    {detail.journal.exit_price ? ` · EXT $${detail.journal.exit_price.toFixed(2)}` : ""}
                  </div>
                  <div className={`ml-auto data-md ${(detail.journal.realized_pnl ?? 0) >= 0 ? "pnl-up" : "pnl-down"}`}>
                    {detail.journal.realized_pnl !== null && detail.journal.realized_pnl !== undefined ? (detail.journal.realized_pnl >= 0 ? "+" : "") + "$" + detail.journal.realized_pnl?.toFixed(2) : "—"}
                  </div>
                </div>
              </div>
              <div>
                <div className="label-cap mb-1">Analyst Thesis</div>
                <p className="text-sm text-muted-foreground">{detail.journal.analyst?.thesis || "—"}</p>
              </div>
              <div>
                <div className="label-cap mb-1">Critic Concerns</div>
                <ul className="text-xs list-disc list-inside space-y-0.5 text-muted-foreground">{(detail.journal.critic?.concerns||[]).map((c,i)=><li key={i}>{c}</li>)}</ul>
              </div>
              <div>
                <div className="label-cap mb-1">Risk Verdict Checks</div>
                <ul className="text-xs space-y-1">
                  {(detail.journal.risk_verdict?.checks||[]).map((c,i)=>(
                    <li key={i} className="flex gap-2">
                      <span className={c.pass ? "pnl-up" : "pnl-down"}>{c.pass ? "✓" : "✗"}</span>
                      <span className="text-muted-foreground">{c.name}</span>
                    </li>
                  ))}
                </ul>
              </div>
              {detail.review ? (
                <div className="border border-border/60 p-3">
                  <div className="label-cap mb-2">Review</div>
                  <div className="text-sm">Outcome: <span className="mono uppercase">{detail.review.outcome.replace("_"," ")}</span></div>
                  <div className="text-sm">Primary cause: <span className="text-muted-foreground">{detail.review.primary_cause}</span></div>
                  {detail.review.tags?.length ? <div className="flex gap-1 flex-wrap mt-2">{detail.review.tags.map(t=><span key={t} className="chip">#{t}</span>)}</div> : null}
                  {detail.review.notes ? <p className="text-xs mt-2 text-muted-foreground">{detail.review.notes}</p> : null}
                </div>
              ) : detail.journal.exit_price ? (
                <button className="btn-primary" onClick={()=>setReviewFor(detail.journal.id)}>Submit Post-Trade Review</button>
              ) : null}
            </div>
          )}
        </section>
      </div>

      <PostTradeReviewModal
        open={!!reviewFor}
        journalId={reviewFor}
        onClose={() => setReviewFor(null)}
        onSubmitted={() => { setReviewFor(null); load(); if (selected) openDetail(selected); }}
      />
    </div>
  );
}
